import { c as _c } from "react/compiler-runtime";
import { graphqlClient } from "@/lib/graphql-client";
import { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphql, type ResultOf } from "@/graphql";
import { useRouter } from "next/router";
import { AUTH_DISABLED_PAGES } from "@/constants/routes";
import { mapCompanyWorkDaysToWeekdayIndices } from "./company-work-days";

/** maps API `settings.weekStart` to date-fns `weekStartsOn` (0 Sun … 6 Sat). */
export function normalizeWeekStartsOn(raw: number | null | undefined): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  if (raw == null || Number.isNaN(raw)) return 1;
  const n = Math.trunc(raw);
  if (n >= 0 && n <= 6) return n as 0 | 1 | 2 | 3 | 4 | 5 | 6;
  // ISO-8601 Sunday is 7; date-fns uses 0 for Sunday
  if (n === 7) return 0;
  return 1;
}
export const CompanySettingsQuery = graphql(`
  query getCompanySettings {
    company {
      id
      timezone
      currency
      dateFormat
      timeFormat
      workDays
      settings {
        eventInvoiceDateSource
        weekStart
      }
      customNaming {
        employee
        employees
        crew
        crews
      }
    }
  }
`);

/**
 * Controls which date lands on invoice line items when an event is closed, and
 * whether the user is prompted to choose at close time. Derived from the
 * `EventInvoiceDateSource` GraphQL enum via the query result.
 */
export type EventInvoiceDateSource = NonNullable<NonNullable<ResultOf<typeof CompanySettingsQuery>["company"]>["settings"]>["eventInvoiceDateSource"];
type CompanySettings = {
  currency: string;
  timezone?: string;
  /** Business date-display preference as a PHP date() format token string. */
  dateFormat?: string;
  /** Business time-display preference as a PHP time() format token string. */
  timeFormat?: string;
  /** How the event-close flow resolves the invoice date. Defaults to
   * `PROMPT_USER` until settings load, preserving the prompt-on-close UX. */
  eventInvoiceDateSource: EventInvoiceDateSource;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Working weekday indices, `0 = Sunday … 6 = Saturday` (same convention as
   * `weekStartsOn`), derived from `company.workDays` via
   * `mapCompanyWorkDaysToWeekdayIndices`. */
  workDays: number[];
  /** Company-configurable terminology overrides, e.g. "Employee" → "Technician".
   * Always non-null strings — falls back to the default English term until
   * `company.customNaming` loads or when a company hasn't set an override. */
  customNaming: {
    employee: string;
    employees: string;
    crew: string;
    crews: string;
  };
};
const DEFAULT_CUSTOM_NAMING = {
  employee: "Employee",
  employees: "Employees",
  crew: "Crew",
  crews: "Crews"
};

/** Used only while `company` hasn't loaded yet. */
const LOADING_DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5];
const CompanySettingsContext = createContext<CompanySettings | undefined>(undefined);
export function useCompanySettings() {
  const context = useContext(CompanySettingsContext);
  if (context === undefined) {
    throw new Error("useCompanySettings must be used within a CompanySettingsProvider");
  }
  return context;
}
export function CompanySettingsProvider(t0) {
  const $ = _c(26);
  const {
    children
  } = t0;
  const router = useRouter();
  const isClient = typeof window !== "undefined";
  let t1;
  if ($[0] !== router) {
    t1 = isClient && router.isReady && !(AUTH_DISABLED_PAGES as readonly string[]).includes(router.pathname);
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shouldFetch = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["company-settings"];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== shouldFetch) {
    t3 = {
      queryKey: t2,
      queryFn: _temp2,
      enabled: shouldFetch
    };
    $[3] = shouldFetch;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: company
  } = useQuery(t3);
  const t4 = company?.currency || "USD";
  const t5 = company?.timezone ?? undefined;
  const t6 = company?.dateFormat ?? undefined;
  const t7 = company?.timeFormat ?? undefined;
  const t8 = company?.settings?.eventInvoiceDateSource ?? "PROMPT_USER";
  const t9 = company?.settings?.weekStart;
  let t10;
  if ($[5] !== t9) {
    t10 = normalizeWeekStartsOn(t9);
    $[5] = t9;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== company) {
    t11 = company?.workDays ? mapCompanyWorkDaysToWeekdayIndices(company.workDays) : LOADING_DEFAULT_WORK_DAYS;
    $[7] = company;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const t12 = company?.customNaming?.employee || DEFAULT_CUSTOM_NAMING.employee;
  const t13 = company?.customNaming?.employees || DEFAULT_CUSTOM_NAMING.employees;
  const t14 = company?.customNaming?.crew || DEFAULT_CUSTOM_NAMING.crew;
  const t15 = company?.customNaming?.crews || DEFAULT_CUSTOM_NAMING.crews;
  let t16;
  if ($[9] !== t12 || $[10] !== t13 || $[11] !== t14 || $[12] !== t15) {
    t16 = {
      employee: t12,
      employees: t13,
      crew: t14,
      crews: t15
    };
    $[9] = t12;
    $[10] = t13;
    $[11] = t14;
    $[12] = t15;
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  let t17;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== t16 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t17 = {
      currency: t4,
      timezone: t5,
      dateFormat: t6,
      timeFormat: t7,
      eventInvoiceDateSource: t8,
      weekStartsOn: t10,
      workDays: t11,
      customNaming: t16
    };
    $[14] = t10;
    $[15] = t11;
    $[16] = t16;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const settings = t17;
  let t18;
  if ($[23] !== children || $[24] !== settings) {
    t18 = <CompanySettingsContext.Provider value={settings}>{children}</CompanySettingsContext.Provider>;
    $[23] = children;
    $[24] = settings;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  return t18;
}
function _temp2() {
  return graphqlClient.request(CompanySettingsQuery).then(_temp);
}
function _temp(res) {
  return res.company;
}